import { ApolloClient, ApolloLink } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { RestLink } from 'apollo-link-rest';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import apolloLoggerLink from 'apollo-link-logger';
import { buildCommonHeadersFromReduxStore } from '@lifeomic/phc-web-toolkit/src/redux/util/headers';
import urls from './util/urls';
import { configureApolloCache } from '@lifeomic/phc-web-toolkit/src/util/configureApolloCache';

export default function configureApolloClient(reduxStore) {
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      for (const { message, locations, path } of graphQLErrors) {
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ` +
            `${locations}, Path: ${path}`,
        );
      }
    }
    if (networkError) {
      console.error(`[Network error]: ${networkError}`);
    }
  });

  const restLink = new RestLink({
    bodySerializers: {
      // bypass default json serializer
      fileUpload: (body, headers) => ({ body, headers }),
    },
    endpoints: {
      dynamicUrlWithEmptyResponse: {
        // we always override this, but the library requires some placeholder.
        // It's dumb about appending full URLs to this. See bug:
        // https://github.com/apollographql/apollo-link-rest/issues/192
        uri: '',
        // unset headers to avoid sending bearer tokens to presigned s3 urls
        headers: {},
        responseTransformer: async response => {
          // apollo-link-rest checks status code before it gets here,
          // we can assume no server/network errors at this point
          return { success: true };
        },
      },
    },
  });

  const batchHttpLink = new BatchHttpLink({
    uri: urls.api.graphQL(),
  });

  const contextLink = setContext((request, existingContext) => {
    const commonHeaders = buildCommonHeadersFromReduxStore(reduxStore);
    return {
      headers: {
        ...commonHeaders,
        ...existingContext.headers,
      },
    };
  });

  // network errors only. Does not retry graphql errors
  const retryLink = new RetryLink({
    delay: { initial: 200, jitter: true },
    attempts: { max: 3 },
  });

  const middelware = [
    process.env.NODE_ENV === 'development' ? apolloLoggerLink : null,
    errorLink,
    retryLink,
    restLink,
    contextLink,
    batchHttpLink,
  ].filter(Boolean);

  return new ApolloClient({
    link: ApolloLink.from(middelware),
    cache: configureApolloCache(),
    connectToDevTools:
      process.env.NODE_ENV === 'development' &&
      process.env.LOCAL_CONNECT_APOLLO_DEV_TOOL === 'true',
  });
}
